import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import ConfirmDialog from "../../components/admin/ConfirmDialog";
import { Plus, Pencil, Trash2, Upload } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Card } from "../../components/ui/card";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import PageHeader from "../../components/admin/PageHeader";
import InfoBanner from "../../components/admin/InfoBanner";
import FilterBar from "../../components/admin/FilterBar";

const BASE_URI = process.env.REACT_APP_API_BASE_URI;
const emptyForm = {
  image: null,
  imagePreview: "",
  title: "",
  description: "",
  button_text: "",
};

const compressImage = (file) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let [w, h] = [img.width, img.height];
        if (w > 800) {
          h *= 800 / w;
          w = 800;
        }
        if (h > 600) {
          w *= 600 / h;
          h = 600;
        }
        canvas.width = w;
        canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        canvas.toBlob(
          (blob) =>
            resolve(
              new File([blob], file.name, {
                type: "image/jpeg",
                lastModified: Date.now(),
              }),
            ),
          "image/jpeg",
          0.9,
        );
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

const ServicesCRUD = () => {
  const [services, setServices] = useState([]);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [editService, setEditService] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 8,
    total: 0,
    total_pages: 1,
  });

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async (page = 1) => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${BASE_URI}/entities?category=service&page=${page}&limit=${pagination.page_size}`,
        getAuthHeaders(),
      );
      if (res.data?.data) {
        setServices(
          res.data.data.map((i) => ({
            id: i.id,
            image: i.data.images?.[0] || "",
            title: i.data.title || "",
            description: i.data.description || "",
            button_text: i.data.button_text || "",
          })),
        );
        setPagination({
          page: res.data.page,
          page_size: res.data.limit,
          total: res.data.total,
          total_pages: Math.ceil(res.data.total / res.data.limit),
        });
      }
      setError("");
    } catch {
      setError("Failed to fetch services");
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setForm(emptyForm);
    setEditService(null);
    setDialogOpen(true);
  };
  const openEdit = (s) => {
    setForm({
      image: null,
      imagePreview: s.image,
      title: s.title,
      description: s.description,
      button_text: s.button_text,
    });
    setEditService(s);
    setDialogOpen(true);
  };

  const handleImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB");
      return;
    }
    const compressed = await compressImage(file);
    const preview = URL.createObjectURL(compressed);
    setForm((f) => ({ ...f, image: compressed, imagePreview: preview }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editService && !form.image) {
      toast.warning("Please upload a service image");
      return;
    }
    if (!form.title.trim() || !form.description.trim()) {
      toast.warning("Please fill all required fields");
      return;
    }
    try {
      setLoading(true);
      const fd = new FormData();
      if (form.image) fd.append("images", form.image);
      fd.append("title", form.title);
      fd.append("description", form.description);
      if (form.button_text) fd.append("button_text", form.button_text);
      const hdrs = {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      };
      if (editService) {
        await axios.put(
          `${BASE_URI}/entities/${editService.id}?category=service`,
          fd,
          hdrs,
        );
        toast.success("Service updated!");
      } else {
        await axios.post(`${BASE_URI}/entities?category=service`, fd, hdrs);
        toast.success("Service created!");
      }
      setDialogOpen(false);
      fetchServices(pagination.page);
    } catch {
      toast.error("Save failed");
    } finally {
      setLoading(false);
    }
  };

  const deleteService = async () => {
    try {
      setDeleteLoading(true);
      await axios.delete(
        `${BASE_URI}/entities/${deleteId}?category=service`,
        getAuthHeaders(),
      );
      toast.success("Deleted successfully!");
      setDeleteId(null);
      fetchServices(
        services.length === 1 && pagination.page > 1
          ? pagination.page - 1
          : pagination.page,
      );
    } catch {
      toast.error("Failed to delete.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const filtered = services.filter((s) =>
    s.title.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Services Management"
        description="Manage services displayed on the homepage"
        action={
          <Button onClick={openAdd}>
            <Plus className="h-4 w-4" /> Add Service
          </Button>
        }
      />
      <InfoBanner
        title="Services Management"
        description="Showcase your organisation's services with images, titles, and call-to-action buttons."
        capabilities={[
          "Image upload with auto-compression",
          "Custom button text",
          "8 services per page",
        ]}
      />
      <FilterBar
        search={search}
        onSearch={setSearch}
        placeholder="Search by title…"
        count={filtered.length}
      />
      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[90px]">Image</TableHead>
              <TableHead>Title</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Button</TableHead>
              <TableHead className="w-[120px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="text-center py-12 text-gray-400"
                >
                  <div className="flex items-center justify-center gap-2">
                    <div className="h-4 w-4 rounded-full border-2 border-blue-400 border-t-transparent animate-spin" />
                    Loading…
                  </div>
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="text-center py-12 text-gray-400"
                >
                  No services yet.
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((s) => (
                <TableRow key={s.id}>
                  <TableCell>
                    {s.image ? (
                      <img
                        src={s.image}
                        alt={s.title}
                        className="h-14 w-20 rounded-lg object-cover border border-gray-100"
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    ) : (
                      <div className="h-14 w-20 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400 text-xs">
                        No img
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="font-medium text-gray-900 align-middle">
                    {s.title}
                  </TableCell>
                  <TableCell className="text-gray-600 text-sm align-middle max-w-[260px]">
                    <p className="line-clamp-2">{s.description}</p>
                  </TableCell>
                  <TableCell className="align-middle">
                    {s.button_text && (
                      <Badge variant="secondary">{s.button_text}</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right align-middle">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEdit(s)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => setDeleteId(s.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-center gap-1 p-4 border-t border-gray-100">
            <Button
              size="sm"
              variant="outline"
              onClick={() => fetchServices(pagination.page - 1)}
              disabled={pagination.page === 1 || loading}
            >
              Previous
            </Button>
            {Array.from(
              { length: pagination.total_pages },
              (_, i) => i + 1,
            ).map((p) => (
              <Button
                key={p}
                size="sm"
                variant={p === pagination.page ? "default" : "outline"}
                onClick={() => fetchServices(p)}
                disabled={loading}
              >
                {p}
              </Button>
            ))}
            <Button
              size="sm"
              variant="outline"
              onClick={() => fetchServices(pagination.page + 1)}
              disabled={pagination.page === pagination.total_pages || loading}
            >
              Next
            </Button>
          </div>
        )}
      </Card>

      <ConfirmDialog
        open={!!deleteId}
        title="Delete service?"
        description="This action cannot be undone."
        onConfirm={deleteService}
        onCancel={() => setDeleteId(null)}
        loading={deleteLoading}
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editService ? "Edit Service" : "Add New Service"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 px-6 py-4">
            <div className="space-y-1.5">
              <Label>
                Service Image{" "}
                {!editService && <span className="text-red-500">*</span>}
              </Label>
              <div className="flex items-center gap-3">
                {form.imagePreview && (
                  <img
                    src={form.imagePreview}
                    alt=""
                    className="h-16 w-24 rounded-lg object-cover border border-gray-200"
                  />
                )}
                <label className="flex items-center gap-2 cursor-pointer rounded-lg border border-dashed border-gray-300 px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50 transition-colors">
                  <Upload className="h-4 w-4" />{" "}
                  {form.imagePreview ? "Change" : "Upload"} image
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImage}
                    className="hidden"
                  />
                </label>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>
                Title <span className="text-red-500">*</span>
              </Label>
              <Input
                placeholder="Service title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label>
                Description <span className="text-red-500">*</span>
              </Label>
              <Textarea
                placeholder="Service description"
                rows={3}
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label>
                Button Text{" "}
                <span className="text-gray-400 text-xs">(optional)</span>
              </Label>
              <Input
                placeholder="e.g. Learn More"
                value={form.button_text}
                onChange={(e) =>
                  setForm({ ...form, button_text: e.target.value })
                }
              />
            </div>
          </form>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={loading}>
              {loading ? "Saving…" : editService ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServicesCRUD;
